import { memo, useEffect, useMemo, useRef, useState } from "react";
import { formatTimecode, type ShowcaseCaption } from "@shared/showcase";
import {
  cueIndexAt,
  parseWebVtt,
  type ShowcaseCue,
} from "@shared/showcaseCaptions";

/**
 * The searchable transcript beside the player: the published caption track,
 * as text. Click a line to jump there; type "price" to find where pricing is
 * talked about. Read from the same WebVTT file the player shows, so the two
 * can never disagree, and nothing is fetched until the page has captions.
 */

const cache = new Map<string, Promise<ShowcaseCue[]>>();

function loadCues(url: string): Promise<ShowcaseCue[]> {
  let p = cache.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) =>
        r.ok ? r.text() : Promise.reject(new Error(String(r.status)))
      )
      .then(parseWebVtt);
    p.catch(() => cache.delete(url));
    cache.set(url, p);
  }
  return p;
}

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const parts: React.ReactNode[] = [];
  const lower = text.toLowerCase();
  let at = 0;
  for (;;) {
    const i = lower.indexOf(query, at);
    if (i < 0) break;
    if (i > at) parts.push(text.slice(at, i));
    parts.push(<mark key={i}>{text.slice(i, i + query.length)}</mark>);
    at = i + query.length;
  }
  parts.push(text.slice(at));
  return <>{parts}</>;
}

const Line = memo(function Line({
  cue,
  active,
  query,
  onSeek,
}: {
  cue: ShowcaseCue;
  active: boolean;
  query: string;
  onSeek: (t: number) => void;
}) {
  return (
    <li>
      <button
        className={`line${active ? " line--active" : ""}`}
        onClick={() => onSeek(cue.start)}
        aria-current={active ? "true" : undefined}
      >
        <span className="line__time">{formatTimecode(cue.start)}</span>
        <span className="line__text">
          <Highlight text={cue.text} query={query} />
        </span>
      </button>
    </li>
  );
});

export function Transcript({
  captions,
  lang,
  time,
  onSeek,
}: {
  captions: ShowcaseCaption[];
  /** The caption language on screen, if any: the transcript follows it. */
  lang: string | null;
  time: number;
  onSeek: (t: number) => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const current =
    captions.find((c) => c.lang === (picked ?? lang)) ?? captions[0] ?? null;
  const [cues, setCues] = useState<ShowcaseCue[] | null>(null);
  const [query, setQuery] = useState("");
  const list = useRef<HTMLOListElement>(null);
  const userScrolledAt = useRef(0);

  // Follow the player's caption choice when it changes.
  useEffect(() => setPicked(null), [lang]);

  useEffect(() => {
    if (!current) return;
    let live = true;
    setCues(null);
    loadCues(current.url)
      .then((c) => live && setCues(c))
      .catch(() => live && setCues([]));
    return () => {
      live = false;
    };
  }, [current?.url]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () => (cues ?? []).filter((c) => !q || c.text.toLowerCase().includes(q)),
    [cues, q]
  );
  const active = cues ? cueIndexAt(cues, time) : -1;
  const activeCue = active >= 0 && cues ? cues[active] : null;

  // Keep the spoken line in view - inside the list, never by scrolling the
  // page, and not while the viewer is reading elsewhere in it.
  useEffect(() => {
    const ol = list.current;
    if (!ol || q || !activeCue) return;
    if (Date.now() - userScrolledAt.current < 4000) return;
    const el = ol.querySelector<HTMLElement>(".line--active");
    if (!el) return;
    const top = el.offsetTop - ol.offsetTop;
    if (
      top < ol.scrollTop ||
      top > ol.scrollTop + ol.clientHeight - el.offsetHeight
    )
      ol.scrollTo({ top: top - ol.clientHeight / 3, behavior: "smooth" });
  }, [activeCue, q]);

  if (!current) return null;

  return (
    <section className="card transcript" aria-label="Transcript">
      <div className="transcript__head">
        <h2 className="card__title">Transcript</h2>
        {captions.length > 1 && (
          <div className="seg" role="group" aria-label="Transcript language">
            {captions.map((c) => (
              <button
                key={c.lang}
                className={c.lang === current.lang ? "is-on" : ""}
                onClick={() => setPicked(c.lang)}
                aria-pressed={c.lang === current.lang}
              >
                {c.lang.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>
      <label className="search">
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path
            d="M11 4a7 7 0 1 0 4.4 12.4l4.1 4.1 1.4-1.4-4.1-4.1A7 7 0 0 0 11 4zm0 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10z"
            fill="currentColor"
          />
        </svg>
        <input
          type="search"
          placeholder={
            current.lang === "sv" ? "Sök i videon" : "Search the video"
          }
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search the transcript"
        />
        {q && cues && (
          <span className="search__count">
            {shown.length === 0 ? "no matches" : shown.length}
          </span>
        )}
      </label>
      {cues === null ? (
        <div className="transcript__loading" aria-label="Loading transcript" />
      ) : (
        <ol
          ref={list}
          className="transcript__lines"
          onWheel={() => (userScrolledAt.current = Date.now())}
          onTouchMove={() => (userScrolledAt.current = Date.now())}
        >
          {shown.map((c) => (
            <Line
              key={c.start}
              cue={c}
              active={c === activeCue}
              query={q}
              onSeek={onSeek}
            />
          ))}
        </ol>
      )}
    </section>
  );
}
